/**
 * Product Detail page -- public. Surfaces everything a buyer needs to decide:
 *   * Image gallery
 *   * Title, condition, stock level, price, shipping
 *   * Seller info + DEALER badge when applicable
 *   * "Buy Now" CTA -> creates Order then redirects to Stripe Checkout
 *   * "Boost this listing ($5)" -> seller-only one-time boost purchase
 */
import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Folder, PackageCheck, Store, Tag, TrendingUp } from "lucide-react";
import { api, extractApiError } from "../../lib/api";
import type { Product, UserPublic } from "../../lib/types";
import { useAuth } from "../../contexts/AuthContext";
import { useLogoContext } from "../../contexts/LogoContext";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { resolveUploadUrl } from "../../components/ui/ImageUploader";
import { ProductImage } from "../../components/ui/ProductImage";
import { CheckoutAddressModal, ShippingAddressValue } from "../../components/ui/CheckoutAddressModal";
import { QuestionsAndAnswers } from "../../components/product/QuestionsAndAnswers";
import { StarRating } from "../../components/reviews/StarRating";

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { productPlaceholderUrl } = useLogoContext();
  const navigate = useNavigate();
  const [product, setProduct] = React.useState<Product | null>(null);
  const [seller, setSeller] = React.useState<UserPublic | null>(null);
  const [activeImg, setActiveImg] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [notFound, setNotFound] = React.useState(false);
  const [buying, setBuying] = React.useState(false);
  const [addressOpen, setAddressOpen] = React.useState(false);
  const [boosting, setBoosting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!id) return;
    setLoading(true);
    (async () => {
      try {
        const r = await api.get<Product>(`/products/${id}`);
        setProduct(r.data);
        // Phase 15: hydrate the seller via the public `/users/{id}`
        // endpoint (added in Phase 8). Closes AUDIT §4.7(l) — the prior
        // try/catch was dead code that always set seller to null, so the
        // DEALER badge below never lit up.
        try {
          const u = await api.get<UserPublic>(`/users/${r.data.seller_id}`);
          setSeller(u.data);
        } catch {
          // Soft failure — the seller_username denormalised on Product
          // still renders. The DEALER badge just won't appear.
          setSeller(null);
        }
      } catch (err: any) {
        if (err?.response?.status === 404) setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="text-3xl font-black mb-2">Listing not found</h1>
        <p className="text-ink-muted">It may have been removed.</p>
      </div>
    );
  }
  if (loading || !product) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16">
        <div className="h-10 w-10 rounded-full border-4 border-surface-border border-t-brand animate-spin mx-auto" />
      </div>
    );
  }

  const isSeller = user?.id === product.seller_id;
  const isFeatured =
    !!product.featured_until && new Date(product.featured_until) > new Date();
  const freeShipping = product.shipping_type === "FREE";

  const buyNow = async () => {
    setError(null);
    if (!user) {
      navigate(`/login?redirect=/product/${product.id}`);
      return;
    }
    // Open the structured-address modal; actual order + Stripe call runs
    // from its submit handler.
    setAddressOpen(true);
  };

  const handleAddressSubmit = async (addr: ShippingAddressValue) => {
    setBuying(true);
    try {
      const order = await api.post("/orders", {
        product_id: product.id,
        quantity: 1,
        shipping_address: addr,
      });
      const checkout = await api.post("/stripe/create-order-checkout", {
        order_id: order.data.id,
        origin_url: window.location.origin,
      });
      window.location.href = checkout.data.url;
    } catch (err) {
      setError(extractApiError(err));
      setAddressOpen(false);
    } finally {
      setBuying(false);
    }
  };

  const boost = async () => {
    setError(null);
    setBoosting(true);
    try {
      const checkout = await api.post("/stripe/create-boost-checkout", {
        target_type: "PRODUCT",
        target_id: product.id,
        origin_url: window.location.origin,
      });
      window.location.href = checkout.data.url;
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setBoosting(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 md:px-8 py-6 md:py-10">
      <div className="grid md:grid-cols-[1.1fr_1fr] gap-8">
        {/* Gallery */}
        <div>
          <div className="aspect-[4/3] bg-surface-sunken rounded-2xl overflow-hidden border border-surface-border relative">
            <ProductImage
              src={product.images[activeImg]}
              alt={product.title}
              className="h-full w-full object-cover"
              placeholderClassName="h-full w-full"
              fallbackSrc={productPlaceholderUrl}
            />
            {isFeatured && (
              <span className="absolute top-3 left-3">
                <Badge variant="brand" size="sm">Featured</Badge>
              </span>
            )}
          </div>
          {product.images.length > 1 && (
            <div className="mt-3 grid grid-cols-5 gap-2">
              {product.images.slice(0, 5).map((src, i) => (
                <button
                  key={src + i}
                  data-testid={`product-thumb-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={
                    "aspect-square rounded-lg overflow-hidden border-2 " +
                    (i === activeImg ? "border-ink" : "border-surface-border")
                  }
                >
                  <img src={resolveUploadUrl(src)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div data-testid="product-detail-info">
          <div className="flex flex-wrap gap-2 mb-3">
            <Badge variant="outline" size="sm">{product.condition}</Badge>
            {seller?.role === "DEALER" && <Badge variant="brand" size="sm">Dealer</Badge>}
            {isFeatured && <Badge variant="soft" size="sm">Featured</Badge>}
          </div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-2">
            {product.title}
          </h1>
          <div className="flex items-center gap-2 text-ink-muted mb-2">
            <Tag className="h-4 w-4" aria-hidden />
            <span className="font-mono text-sm">{product.part_number}</span>
            <span>·</span>
            <span className="text-sm">{product.stock_quantity} in stock</span>
          </div>

          {/* Phase 27 — forum category breadcrumb. Both the brand and
              subforum names are independent links; absent when the
              listing has no category or it was deleted. */}
          {product.category && (
            <div
              data-testid="product-forum-breadcrumb"
              className="flex items-center gap-1.5 text-ink-muted mb-6 text-sm flex-wrap"
            >
              <Folder className="h-4 w-4" aria-hidden />
              <span>Forum:</span>
              {product.category.parent_name && product.category.parent_slug && (
                <>
                  <Link
                    to={`/forums/c/${product.category.parent_slug}`}
                    className="font-semibold text-ink hover:underline"
                  >
                    {product.category.parent_name}
                  </Link>
                  <span aria-hidden>›</span>
                </>
              )}
              <Link
                to={`/forums/c/${product.category.slug}`}
                className="font-semibold text-ink hover:underline"
              >
                {product.category.name}
              </Link>
            </div>
          )}

          <div className="flex items-end gap-4 mb-4">
            <span className="font-display text-5xl font-black text-ink leading-none">
              ${product.price.toFixed(2)}
            </span>
          </div>
          <p className="text-sm text-ink-muted mb-6 inline-flex items-center gap-2">
            <PackageCheck className="h-4 w-4" aria-hidden />
            {freeShipping
              ? "Free shipping included."
              : `+ $${product.shipping_cost.toFixed(2)} shipping (buyer pays).`}
          </p>

          <div className="rounded-xl border border-surface-border bg-surface-raised px-4 py-3 flex items-center gap-3 mb-6">
            <Store className="h-5 w-5 text-ink-muted" aria-hidden />
            <div className="text-sm flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>
                Sold by <strong>@{product.seller_username ?? "seller"}</strong>
              </span>
              {/* Phase 17 — seller rating chip. `seller` hydrates async; when
                  the projection includes review_count we render either the
                  stars or a muted "No reviews yet". */}
              {seller && (
                <StarRating
                  value={seller.average_rating ?? null}
                  count={seller.review_count ?? 0}
                  size={14}
                />
              )}
              {/* Phase 31-D — public dealer contact details, only shown
                  when set (older/admin-granted dealers may not have them). */}
              {seller?.phone_number && (
                <span data-testid="product-seller-phone">📞 {seller.phone_number}</span>
              )}
              {seller?.contact_email && (
                <a
                  href={`mailto:${seller.contact_email}`}
                  className="underline"
                  data-testid="product-seller-contact-email"
                >
                  ✉️ {seller.contact_email}
                </a>
              )}
            </div>
          </div>

          {error && (
            <div
              role="alert"
              data-testid="product-detail-error"
              className="mb-4 rounded-xl border-2 border-brand bg-brand-softer text-brand-ink px-3 py-2 text-sm font-medium"
            >
              {error}
            </div>
          )}

          <div className="flex flex-col gap-3">
            <Button
              variant="primary"
              size="lg"
              fullWidth
              loading={buying}
              onClick={buyNow}
              data-testid="buy-now-btn"
            >
              Buy now — ${(product.price + (freeShipping ? 0 : product.shipping_cost)).toFixed(2)}
            </Button>
            {isSeller && (
              <Button
                variant="outline"
                size="lg"
                fullWidth
                loading={boosting}
                onClick={boost}
                leadingIcon={<TrendingUp className="h-5 w-5" />}
                data-testid="boost-listing-btn"
              >
                Boost this listing ($5 · 7 days)
              </Button>
            )}
          </div>

          <p className="mt-6 text-ink whitespace-pre-line leading-relaxed">
            {product.description}
          </p>

          {/* Phase 15 — Compatibility / Fitment ----------------- */}
          {(product.compatibility_notes?.trim() ||
            (product.fitment_tags && product.fitment_tags.length > 0)) && (
            <section
              data-testid="product-fitment"
              className="mt-6 rounded-2xl border border-surface-border bg-surface-raised p-4"
            >
              <h2 className="text-sm font-bold uppercase tracking-wider text-ink-muted mb-2">
                Compatibility
              </h2>
              {product.compatibility_notes?.trim() && (
                <p
                  data-testid="product-compatibility-notes"
                  className="text-ink whitespace-pre-line leading-relaxed"
                >
                  {product.compatibility_notes}
                </p>
              )}
              {product.fitment_tags && product.fitment_tags.length > 0 && (
                <div
                  data-testid="product-fitment-tags"
                  className="mt-3 flex flex-wrap gap-2"
                >
                  {product.fitment_tags.map((t) => (
                    <span
                      key={t}
                      className="inline-flex items-center rounded-full bg-ink text-white text-xs font-semibold px-3 py-1"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </section>
          )}
        </div>
      </div>

      {/* Phase 16 — Q&A lives full-width below the two-column hero so
          the discussion has room to breathe on desktop. */}
      <QuestionsAndAnswers productId={product.id} sellerId={product.seller_id} />

      <CheckoutAddressModal
        open={addressOpen}
        onClose={() => setAddressOpen(false)}
        onSubmit={handleAddressSubmit}
        busy={buying}
      />
    </div>
  );
}
